import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, FileText, AlertTriangle, Scale, Ban, CreditCard, Mail } from 'lucide-react';

export default function TermsOfService() {
    const lastUpdated = "February 28, 2025";
    
    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="terms-page">
            <div className="max-w-4xl mx-auto px-4">
                <Link to="/">
                    <Button variant="ghost" className="mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </Link>

                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">Terms of Service</h1>
                        <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
                    </div>
                </div>

                {/* Important Medical Disclaimer */}
                <Card className="mb-6 border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                            <div>
                                <h2 className="font-heading text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
                                    Important Medical Disclaimer
                                </h2>
                                <p className="text-amber-700 dark:text-amber-300">
                                    <strong>MediGuide AI is NOT a substitute for professional medical advice, diagnosis, or treatment.</strong> 
                                    The information provided by this application is for general informational purposes only. 
                                    Always seek the advice of your physician or other qualified health provider with any 
                                    questions you may have regarding a medical condition. Never disregard professional 
                                    medical advice or delay in seeking it because of something you have read or received from this application.
                                </p>
                                <p className="text-amber-700 dark:text-amber-300 mt-3">
                                    <strong>In case of a medical emergency, call your local emergency services immediately.</strong>
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardContent className="p-6">
                        <p className="text-muted-foreground">
                            By accessing or using MediGuide AI ("the Service"), you agree to be bound by these Terms of Service. 
                            If you disagree with any part of these terms, you may not access the Service.
                        </p>
                    </CardContent>
                </Card>

                <div className="space-y-6">
                    {/* Acceptance of Terms */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
                            <p className="text-muted-foreground mb-3">
                                By creating an account or using MediGuide AI, you acknowledge that you have read, 
                                understood, and agree to be bound by these Terms of Service and our Privacy Policy.
                            </p>
                            <p className="text-muted-foreground">
                                You must be at least 18 years old to use this Service. If you are between 13 and 18 years old, 
                                you may only use the Service under the supervision of a parent or legal guardian who agrees to these Terms.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Description of Service */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">2. Description of Service</h2>
                            <p className="text-muted-foreground mb-3">
                                MediGuide AI provides:
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li>AI-powered symptom assessment and health information</li>
                                <li>Health education resources and patient information leaflets</li>
                                <li>Health diary and tracking features</li>
                                <li>Location-based healthcare facility finder</li>
                                <li>Tools to share health information with healthcare providers</li>
                            </ul>
                            <p className="text-muted-foreground mt-4">
                                The Service uses artificial intelligence to analyze symptoms and provide general health information. 
                                The AI is designed to assist, not replace, professional medical judgment.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Medical Disclaimer */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                                <h2 className="font-heading text-xl font-semibold">3. Medical Disclaimer</h2>
                            </div>
                            <div className="space-y-3 text-muted-foreground">
                                <p>
                                    <strong>3.1 Not Medical Advice:</strong> The content provided by MediGuide AI is for 
                                    informational purposes only and is not intended to be a substitute for professional 
                                    medical advice, diagnosis, or treatment.
                                </p>
                                <p>
                                    <strong>3.2 No Doctor-Patient Relationship:</strong> Use of this Service does not 
                                    create a doctor-patient relationship between you and MediGuide AI or any healthcare provider.
                                </p>
                                <p>
                                    <strong>3.3 AI Limitations:</strong> Our AI system has limitations and may not 
                                    always provide accurate assessments. It cannot examine you physically, order tests, 
                                    or prescribe medications.
                                </p>
                                <p>
                                    <strong>3.4 Emergency Situations:</strong> If you are experiencing a medical emergency, 
                                    call emergency services (911 in the US, 999 in the UK, 112 in EU) immediately. 
                                    Do not rely on this app for emergency medical situations.
                                </p>
                                <p>
                                    <strong>3.5 Professional Consultation:</strong> Always consult with a qualified 
                                    healthcare professional for medical advice, diagnosis, and treatment.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* User Responsibilities */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">4. User Responsibilities</h2>
                            <p className="text-muted-foreground mb-3">You agree to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li>Provide accurate information about your symptoms and medical history</li>
                                <li>Use the Service only for its intended purpose</li>
                                <li>Not rely solely on the Service for medical decisions</li>
                                <li>Seek professional medical help when needed</li>
                                <li>Keep your account credentials secure</li>
                                <li>Not share your account with others</li>
                                <li>Not use the Service for any illegal purpose</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Prohibited Uses */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Ban className="w-5 h-5 text-destructive" />
                                <h2 className="font-heading text-xl font-semibold">5. Prohibited Uses</h2>
                            </div>
                            <p className="text-muted-foreground mb-3">You may NOT use the Service to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li>Diagnose or treat medical conditions without professional guidance</li>
                                <li>Submit false or misleading health information</li>
                                <li>Attempt to reverse engineer or hack the Service</li>
                                <li>Scrape data or overload our servers</li>
                                <li>Impersonate others or create fake accounts</li>
                                <li>Violate any applicable laws or regulations</li>
                                <li>Harass, abuse, or harm others</li>
                                <li>Use the Service for commercial purposes without permission</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Subscription and Payments */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <CreditCard className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">6. Subscription and Payments</h2>
                            </div>
                            <div className="space-y-3 text-muted-foreground">
                                <p>
                                    <strong>6.1 Free Trial:</strong> We offer a limited free trial. After the trial, 
                                    some features may require a paid subscription.
                                </p>
                                <p>
                                    <strong>6.2 Subscription Plans:</strong> Paid subscriptions are billed according 
                                    to the plan selected (monthly or annually).
                                </p>
                                <p>
                                    <strong>6.3 Cancellation:</strong> You may cancel your subscription at any time. 
                                    Cancellation takes effect at the end of the current billing period.
                                </p>
                                <p>
                                    <strong>6.4 Refunds:</strong> Refunds are handled on a case-by-case basis. 
                                    Contact support for refund requests.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Intellectual Property */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">7. Intellectual Property</h2>
                            <p className="text-muted-foreground mb-3">
                                All content, features, and functionality of the Service, including but not limited to 
                                text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, 
                                and software, are the exclusive property of MediGuide AI and are protected by copyright, 
                                trademark, and other intellectual property laws.
                            </p>
                            <p className="text-muted-foreground">
                                You are granted a limited, non-exclusive, non-transferable license to use the Service 
                                for personal, non-commercial purposes only.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Limitation of Liability */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Scale className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">8. Limitation of Liability</h2>
                            </div>
                            <div className="space-y-3 text-muted-foreground">
                                <p>
                                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, MEDIGUIDE AI SHALL NOT BE LIABLE FOR ANY 
                                    INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT 
                                    NOT LIMITED TO:
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-4">
                                    <li>Loss of profits, data, or goodwill</li>
                                    <li>Personal injury or death</li>
                                    <li>Medical complications or misdiagnosis</li>
                                    <li>Service interruptions or errors</li>
                                    <li>Any damages arising from your use of the Service</li>
                                </ul>
                                <p className="mt-3">
                                    Our total liability shall not exceed the amount you paid for the Service in the 
                                    12 months preceding the claim.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Indemnification */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">9. Indemnification</h2>
                            <p className="text-muted-foreground">
                                You agree to indemnify, defend, and hold harmless MediGuide AI, its officers, directors, 
                                employees, agents, and affiliates from and against any claims, liabilities, damages, 
                                losses, and expenses, including reasonable attorney fees, arising out of or in any way 
                                connected with your access to or use of the Service, your violation of these Terms, 
                                or your violation of any rights of another.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Termination */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">10. Termination</h2>
                            <p className="text-muted-foreground mb-3">
                                We may terminate or suspend your account and access to the Service immediately, 
                                without prior notice or liability, for any reason, including if you breach these Terms.
                            </p>
                            <p className="text-muted-foreground">
                                Upon termination, your right to use the Service will cease immediately. 
                                You may request deletion of your data as outlined in our Privacy Policy.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Governing Law */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">11. Governing Law</h2>
                            <p className="text-muted-foreground">
                                These Terms shall be governed by and construed in accordance with the laws of the 
                                jurisdiction in which MediGuide AI operates, without regard to its conflict of law provisions.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Changes to Terms */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">12. Changes to Terms</h2>
                            <p className="text-muted-foreground">
                                We reserve the right to modify or replace these Terms at any time. If a revision is 
                                material, we will provide at least 30 days notice prior to any new terms taking effect. 
                                What constitutes a material change will be determined at our sole discretion. 
                                By continuing to use the Service after revisions become effective, you agree to be 
                                bound by the revised terms.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Contact */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Mail className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">13. Contact Us</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                If you have any questions about these Terms, please contact us:
                            </p>
                            <div className="bg-muted/50 rounded-lg p-4">
                                <p className="font-medium">MediGuide AI</p>
                                <p className="text-muted-foreground">Email: legal@mediguide.online</p>
                                <p className="text-muted-foreground">Website: https://mediguide.online</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>© {new Date().getFullYear()} MediGuide AI. All rights reserved.</p>
                    <p className="mt-2">
                        By using MediGuide AI, you acknowledge that you have read and understood these Terms of Service.
                    </p>
                </div>
            </div>
        </div>
    );
}
